import { useEffect, useMemo, useCallback } from "react";
import _ from "underscore";

import App from "app";
// @ts-ignore
import api from "core/api/api";

import { IActionBtn } from "../fd-form-builder/action-button";
import {
    getDatabaseType,
    isNoSQLAdapter
} from "../../../app/components/common/editor/control/connection-text-field/connection-test-view";
import lastConnectionTestRepository, {
    connectionStatuses
} from "../../../app/components/common/editor/control/connection-text-field/last-connection-test-repository";
import {
    AppVentFDAdapterConnectionFieldsChanged,
    AppVentFDAdapterConnectionFieldsRevert,
    AppVentFDAdapterOnChange,
    AppVentFDAdapterTestConnectionStatus
} from "../fd-form-builder/fd-form-builder.utils";
import { getValidationDetails, getDateTime } from "./test-connection-helper";
import TestConnectionMsgBox from "./test-connection-msg-box";
import TestConnectionForAgent from "./test-connection-for-agent";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import store from "src/stores/redux/store";
import {
    setShowAgentModalAction,
    setTestConnectionLoadingAction,
    setTestConnectionErrorMsgAction,
    setConnectionSuccessAction,
    setLastValidationDateAction,
    setUnavailableDriverDBTypeAction,
    setLastOnTestValidationDtlsAction,
    setCurrentValidationDtlsAction
} from "src/stores/redux/features/sidebar/sidebar.slice";

export enum TestConnectionStatus {
    Default = null,
    Success = "success",
    Fail = "fail",
    Loading = "loading",
    DriverNotFound = "driver-not-found"
}
interface TestConnectionProps {
    formRef: object;
    propertiesModel?: object;
    metaObjectId?: string;
    handler: string;
    isFormEnabled: boolean;
    buttonProps: IActionBtn;
}

type DeploymentGroup = {
    label: string;
    value: string;
};

export interface ValidationDtls {
    Username: string;
    ConnectionURL: string;
    adapterName: string;
    Password: string | object;
    encrypted: boolean;
    SSLConfig: string;
    databaseProviderType: string;
    agentDeploymentGroups: DeploymentGroup[];
    databaseType: string;
}

const useApplicationHelperAPI = path => {
    const ApplicationHelperRegex = /^com.webaction.web.api.ApplicationHelper/m;
    return ApplicationHelperRegex.test(path);
};

const checkDriver = async (databaseType: string) => {
    const driverExists = await api.checkDrivers(databaseType);
    return !driverExists;
};

const TestConnection = ({
    propertiesModel,
    metaObjectId,
    handler,
    formRef,
    isFormEnabled,
    buttonProps
}: TestConnectionProps) => {
    const adapter = handler?.split(".")?.pop();

    const isApplicationHelperAPI = useApplicationHelperAPI(buttonProps.path);
    let validationDtls = getValidationDetails({
        propertiesModel,
        handler,
        formRef,
        apiParams: buttonProps.parameters,
        isApplicationHelperAPI
    });

    const dispatch = useAppDispatch();
    const {
        showAgentModal,
        loading,
        errorMsg,
        connectionSuccess,
        lastValidationDate,
        unavailableDriverDBType,
        lastOnTestValidationDtls,
        currentValidationDtls
    } = useAppSelector(state => state.sidebar.testConnectionData);

    const setShowAgentModal: React.Dispatch<React.SetStateAction<boolean>> = value =>
        dispatch(setShowAgentModalAction(typeof value === "function" ? value(showAgentModal) : value));
    const setLoading = (value: boolean) => dispatch(setTestConnectionLoadingAction(value));
    const setErrorMsg = (value: string | null) => dispatch(setTestConnectionErrorMsgAction(value));
    const setConnectionSuccess = (value: boolean) => dispatch(setConnectionSuccessAction(value));
    const setLastValidationDate = (value: string | null) => dispatch(setLastValidationDateAction(value));
    const setUnavailableDriverDBType = (value: string | null) => dispatch(setUnavailableDriverDBTypeAction(value));
    const setLastOnTestValidationDtls = (value: ValidationDtls | undefined) =>
        dispatch(setLastOnTestValidationDtlsAction(value));
    const setCurrentValidationDtls = (value: ValidationDtls | undefined) =>
        dispatch(setCurrentValidationDtlsAction(value));

    const constructRequestParams = validationDtls => {
        let request: object | undefined;

        if (buttonProps.parameters_type === "MAP") {
            request = {};
            Object.values(buttonProps.parameters).forEach(key => {
                request[key] = validationDtls[key];
            });
        }

        return [request];
    };

    const getConnectionPropertiesValues = (values): ValidationDtls => {
        let connectionPropertiesValues = {};
        const connectionProperties = Object.values(buttonProps.parameters);
        Object.entries(values).forEach(([key, value]) => {
            if (connectionProperties.includes(key)) connectionPropertiesValues[key] = value;
        });
        return connectionPropertiesValues as ValidationDtls;
    };

    const onUpdateValidationDtls = useCallback(() => {
        const freshValidationDtls = getValidationDetails({
            propertiesModel,
            handler,
            formRef,
            apiParams: buttonProps.parameters,
            isApplicationHelperAPI
        }) as ValidationDtls;
        setCurrentValidationDtls(getConnectionPropertiesValues(formRef?.current?.values));
        return freshValidationDtls;
    }, []);

    const testConnection = (agentDeploymentGroup = "") => {
        validationDtls = onUpdateValidationDtls();
        setLastOnTestValidationDtls(getConnectionPropertiesValues(formRef?.current?.values));
        App.vent.trigger(AppVentFDAdapterTestConnectionStatus, TestConnectionStatus.Loading);

        const { databaseType } = validationDtls;

        (async () => {
            let validationResponse: { result: boolean; message: string };
            let isDriverUnavailable = false;
            const testedDate = getDateTime();
            const isRDBMSAdapter =
                getDatabaseType(adapter) || adapter === "DatabaseReader" || adapter === "IncrementalBatchReader";
            setErrorMsg(null);
            setConnectionSuccess(false);
            setLastValidationDate(null);
            setUnavailableDriverDBType(null);
            setLoading(true);
            let params = constructRequestParams(validationDtls);
            const adapterProperties = params[0];
            try {
                if (isRDBMSAdapter) {
                    if (databaseType) {
                        isDriverUnavailable = await checkDriver(databaseType);
                    }
                    setUnavailableDriverDBType(isDriverUnavailable ? databaseType : null);
                    if (isDriverUnavailable) {
                        validationResponse = {
                            result: false,
                            message: databaseType
                        };
                        return;
                    }
                    params = [api.token, databaseType, ...params, agentDeploymentGroup, metaObjectId || null];
                }

                if (isNoSQLAdapter(adapter)) {
                    params = [...params, agentDeploymentGroup];
                }

                let res;
                if (isApplicationHelperAPI) {
                    validationResponse = await api.call(buttonProps.path, [...params]);
                } else {
                    res = await api.executePropertyAction(
                        metaObjectId,
                        handler,
                        buttonProps.path,
                        adapterProperties,
                        agentDeploymentGroup
                    );
                    validationResponse = JSON.parse(res);
                }
                if (validationResponse?.result) {
                    setConnectionSuccess(!!validationResponse.message);
                } else {
                    setErrorMsg(validationResponse.message);
                }
                setLastValidationDate(testedDate);
            } catch (e) {
                validationResponse = {
                    result: false,
                    message: e?.message
                };
                console.error(e);
                setErrorMsg(e?.message || null);
            } finally {
                const status = isDriverUnavailable
                    ? connectionStatuses.driversNotFound
                    : validationResponse?.result
                    ? connectionStatuses.ok
                    : connectionStatuses.connectionFailed;
                if (metaObjectId) {
                    lastConnectionTestRepository.save(metaObjectId, handler, {
                        status,
                        validationDate: testedDate,
                        message: validationResponse?.message
                    });
                }
                App.vent.trigger(
                    AppVentFDAdapterTestConnectionStatus,
                    status === connectionStatuses.driversNotFound
                        ? TestConnectionStatus.DriverNotFound
                        : status === connectionStatuses.connectionFailed
                        ? TestConnectionStatus.Fail
                        : TestConnectionStatus.Success
                );
                App.vent.trigger("component:validation", {
                    modelId: metaObjectId || "temp-node",
                    status,
                    message: validationResponse?.message
                });
                setLoading(false);
            }
        })();
    };

    const handleTestConnectionClick = () => {
        if (validationDtls?.agentDeploymentGroups?.length > 0) {
            setShowAgentModal(true);
        } else {
            testConnection();
        }
    };

    const isModified = useMemo(() => {
        return !_.isEqual(lastOnTestValidationDtls, currentValidationDtls);
    }, [lastOnTestValidationDtls, currentValidationDtls]);

    const hasNonPasswordFieldChanges = useMemo(() => {
        const propertiesToIgnore = ["Password"];
        const lastOnTestValidationDtlsWithoutPassword = _.omit(lastOnTestValidationDtls, propertiesToIgnore);
        const validationDtlsWithoutPassword = _.omit(currentValidationDtls, propertiesToIgnore);
        return (
            !_.isEmpty(lastOnTestValidationDtlsWithoutPassword) &&
            !_.isEqual(lastOnTestValidationDtlsWithoutPassword, validationDtlsWithoutPassword)
        );
    }, [lastOnTestValidationDtls, currentValidationDtls]);

    useEffect(() => {
        (async () => {
            const cachedTestConnectionData = store.getState().sidebar.testConnectionData;
            if (cachedTestConnectionData.lastOnTestValidationDtls) {
                return;
            }
            const validationStatus = await lastConnectionTestRepository.load(metaObjectId, handler);
            const validationDetails = getConnectionPropertiesValues(formRef?.current?.values);
            switch (validationStatus?.status) {
                case connectionStatuses.ok: {
                    setConnectionSuccess(true);
                    App.vent.trigger(AppVentFDAdapterTestConnectionStatus, TestConnectionStatus.Success);
                    setLastValidationDate(validationStatus.validationDate || null);
                    setCurrentValidationDtls(validationDetails);
                    setLastOnTestValidationDtls(validationDetails);
                    break;
                }
                case connectionStatuses.connectionFailed: {
                    setErrorMsg(validationStatus.message);
                    App.vent.trigger(AppVentFDAdapterTestConnectionStatus, TestConnectionStatus.Fail);
                    setLastValidationDate(validationStatus.validationDate || null);
                    setCurrentValidationDtls(validationDetails);
                    setLastOnTestValidationDtls(validationDetails);
                    break;
                }
                case connectionStatuses.driversNotFound: {
                    App.vent.trigger(AppVentFDAdapterTestConnectionStatus, TestConnectionStatus.DriverNotFound);
                    setUnavailableDriverDBType(validationStatus.message);
                    break;
                }
                default: {
                    setErrorMsg(null);
                    setConnectionSuccess(false);
                    setUnavailableDriverDBType(null);
                    App.vent.trigger(AppVentFDAdapterTestConnectionStatus, TestConnectionStatus.Default); // Can be new connection
                }
            }
        })();

        App.vent.on(AppVentFDAdapterOnChange, async () => onUpdateValidationDtls());

        return () => {
            App.vent.off(AppVentFDAdapterOnChange);
        };
    }, []);

    useEffect(() => {
        App.vent.on(AppVentFDAdapterConnectionFieldsRevert, async () => {
            if (formRef?.current?.values) {
                formRef.current.values.isReverting = true;
            }
            Object.keys(lastOnTestValidationDtls ?? {}).forEach(key => {
                let prevValue = lastOnTestValidationDtls[key];
                const currValue = formRef.current.values[key];
                if (_.isEqual(currValue, prevValue)) return;
                if (currValue?.Encrypted || currValue?.encrypted) {
                    return;
                }
                formRef.current.setFieldValue(key, prevValue);
            });
            if (formRef?.current?.values) {
                formRef.current.values.isReverting = false;
            }
            setCurrentValidationDtls(lastOnTestValidationDtls);
        });

        return () => App.vent.off(AppVentFDAdapterConnectionFieldsRevert);
    }, [formRef, lastOnTestValidationDtls, validationDtls]);

    useEffect(() => {
        if (hasNonPasswordFieldChanges) App.vent.trigger(AppVentFDAdapterConnectionFieldsChanged);
    }, [hasNonPasswordFieldChanges]);

    if (!isFormEnabled) {
        return null;
    }

    return (
        <>
            {showAgentModal && (
                <TestConnectionForAgent
                    validationDtls={validationDtls}
                    setShowAgentModal={setShowAgentModal}
                    showAgentModal={showAgentModal}
                    testConnection={testConnection}
                    loading={loading}
                    errorMsg={errorMsg ?? undefined}
                    connectionSuccess={connectionSuccess}
                    unavailableDriverDBType={unavailableDriverDBType ?? undefined}
                />
            )}
            <TestConnectionMsgBox
                loading={loading}
                testConnection={handleTestConnectionClick}
                errorMsg={errorMsg ?? undefined}
                connectionSuccess={connectionSuccess}
                lastValidationDate={lastValidationDate ?? ""}
                unavailableDriverDBType={unavailableDriverDBType ?? undefined}
                testCxnBtnName={buttonProps?.name}
                handler={handler}
                isModified={isModified}
            />
        </>
    );
};
export default TestConnection;
